import React, {useCallback, useEffect, useMemo, useRef, useState} from "react"
import {createPortal} from "react-dom"
import {useParams} from "react-router-dom"
import moment from "moment"
import "moment/locale/fr"
import parse from "html-react-parser"
import {FacebookIcon, FacebookShareButton, TumblrIcon, TumblrShareButton, WhatsappIcon, WhatsappShareButton,} from "react-share"

import {getEventDetail, getEventPartnerDetail, reportAnEvent, setVisit,} from "../../api/eventActions"
import {getImageByEvent} from "../../api/firebaseActions"
import config from "../../config"
import {cleanText, getFormattedDate} from "../../utils/functions"
import {locales} from "../../utils/locale/MomentLocale"
import MiniMap from "../miniMap/MiniMap"
import "./Detail.css"
import {getImageByCompany} from "../../api/companyActions"

const BASE_URL = config.BASE_URL


function getFullCoverImageUrl(response) {
    if (!response?.images?.length) return null
    console.log("getFullCoverImageUrl response:", response)
    console.log("getFullCoverImageUrl result:", response.images.find(img => img.isCover === true) || response.images[0])
    return response.images.find(img => img.isCover === true) || response.images[0]
}

function getGalleryImagesUrl(response) {
    if (!response?.images?.length) return []
    return response.images.filter(img => !img.isCover)
}

function detectMediaType(uri = "", mime = "") {
    if (!uri) return "image"
    if (mime.startsWith("video/")) return "video"
    if (mime.startsWith("image/")) return "image"
    const lower = uri.toLowerCase()
    try {
        const decoded = decodeURIComponent(lower)
        if (/\.(mp4|mov|avi|mkv|webm)/.test(decoded)) return "video"
    } catch { /* ignore */
    }
    if (/\.(mp4|mov|avi|mkv|webm)/.test(lower)) return "video"
    return "image"
}

// Business logic helpers

function buildEventImageUrl(isXml, eid, imageName, partnerImage, updatedAt) {
    if (isXml === "1") return partnerImage
    if (imageName) return `${BASE_URL}img/events/${imageName}?${updatedAt}`
    return `${BASE_URL}img/events/cover-${eid}.jpg?${updatedAt}`
}

function buildCompanyImageUrl(cid) {
    return cid ? `${BASE_URL}img/company/cover-${cid}.jpg` : ""
}


function formatEventDate(isXml, dateFrom, dateTo) {
    if (isXml === "1") {
        if (getFormattedDate(dateFrom, "after")) {
            return `Jusqu'au ${moment(dateTo).format("HH:mm")}`
        }
        return getFormattedDate(dateFrom, "start")
    }
    return `${getFormattedDate(dateFrom, "start")} à ${moment(dateTo).format("HH:mm")}`
}

function buildPriceLabel(isFree, tarif, tarifPromo) {
    if (isFree) return "Gratuit"
    if (tarif > 0 && tarifPromo > 0)
        return {original: `${tarif}€`, promo: `${tarifPromo}€`}
    if (tarif > 0) return `${tarif}€`
    return null
}


function GalleryLightbox({images, initialIndex = 0, onClose}) {
    const [currentIndex, setCurrentIndex] = useState(initialIndex)
    const media = images[currentIndex]

    const handlePrev = useCallback(() => {
        setCurrentIndex(i => (i > 0 ? i - 1 : images.length - 1))
    }, [images.length])

    const handleNext = useCallback(() => {
        setCurrentIndex(i => (i < images.length - 1 ? i + 1 : 0))
    }, [images.length])

    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Escape") {
                e.stopImmediatePropagation();
                onClose()
            }
            if (e.key === "ArrowLeft") handlePrev()
            if (e.key === "ArrowRight") handleNext()
        }
        document.body.style.overflow = "hidden"
        window.addEventListener("keydown", handler)
        return () => {
            document.body.style.overflow = ""
            window.removeEventListener("keydown", handler)
        }
    }, [onClose, handlePrev, handleNext])

    if (!media) return null

    return createPortal(
        <div className="dt-lightbox" onClick={onClose}>
            <div className="dt-lightbox__content" onClick={e => e.stopPropagation()}>
                <button className="dt-lightbox__close" onClick={onClose} aria-label="Fermer">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </button>

                {images.length > 1 && (
                    <button className="dt-lightbox__nav dt-lightbox__nav--prev" onClick={handlePrev}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6"/>
                        </svg>
                    </button>
                )}

                {media.type === "video" ? (
                    <video className="dt-lightbox__video" src={media.uri} controls autoPlay/>
                ) : (
                    <img className="dt-lightbox__img" src={media.uri} alt={`Media ${currentIndex + 1}`}/>
                )}

                {images.length > 1 && (
                    <button className="dt-lightbox__nav dt-lightbox__nav--next" onClick={handleNext}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 18 15 12 9 6"/>
                        </svg>
                    </button>
                )}

                <div className="dt-lightbox__counter">{currentIndex + 1} / {images.length}</div>
            </div>
        </div>,
        document.body
    )
}


function GalleryStrip({mediaList, onOpen}) {
    const scrollRef = useRef(null)
    if (!mediaList || mediaList.length === 0) return null

    return (
        <section className="dt-gallery">
            <div className="dt-gallery__header">
                <span className="dt-gallery__title">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                    </svg>
                    Photos &amp; Vidéos
                </span>
                <span className="dt-gallery__count">{mediaList.length}</span>
            </div>
            <div className="dt-gallery__strip" ref={scrollRef}>
                {mediaList.map((media, index) => (
                    <button
                        key={media.id || index}
                        className={`dt-gallery__thumb ${media.isCover ? "dt-gallery__thumb--cover" : ""}`}
                        onClick={() => {
                            console.log("Open media", {index, media})
                            onOpen(index)
                        }}
                    >
                        {media.type === "video" ? (
                            <video
                                src={media.thumbnail || media.url || media.uri}
                                className="dt-gallery__thumb-media"
                                preload="metadata"
                                muted
                                playsInline
                            />
                        ) : (
                            <img
                                src={media.thumbnail || media.url || media.uri}
                                alt={`Media ${index + 1}`}
                                loading="lazy"
                                className="dt-gallery__thumb-media"
                            />
                        )}

                        {media.isCover && <span className="dt-gallery__badge">Cover</span>}
                        {media.type === "video" && (
                            <div className="dt-gallery__play">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                                    <polygon points="5 3 19 12 5 21 5 3"/>
                                </svg>
                            </div>
                        )}
                    </button>
                ))}
            </div>
        </section>
    )
}


const initialEvent = {
    title: "", description: "", imageName: "", partnerImage: "",
    tarif: "", tarifPromo: "", dtFrom: "", dtTo: "",
    isXml: "", isFree: false, deeplink: "", updatedAt: "",
    position: null, isFirebase: false,
}

const initialCompany = {
    name: "", phone: "", detail: "", address: "", city: "", id: null,
}


/**
 * Fonctionne en deux modes :
 *  - Mode page  : eid/xml depuis useParams(), pas de onClose → window.close()
 *  - Mode modal : eid/xml passés en props,    onClose ferme le modal dans Feed
 */
export default function Detail({eid: eidProp, xml: xmlProp, onClose: onCloseProp}) {
    const params = useParams()
    const eid = String(eidProp ?? params.eid ?? "")
    const [cid, setCid] = useState(null)
    const xml = String(xmlProp ?? params.xml ?? "0")
    const [event, setEvent] = useState(initialEvent)
    const [company, setCompany] = useState(initialCompany)
    const [isReported, setIsReported] = useState(false)
    const [loading, setLoading] = useState(true)
    const [showReportConfirm, setShowReportConfirm] = useState(false)
    const [mediaList, setMediaList] = useState([])
    const [societyMediaList, setSocietyMediaList] = useState([])
    const [lightboxOpen, setLightboxOpen] = useState(false)
    const [lightboxImages, setLightboxImages] = useState(null)
    const [lightboxIndex, setLightboxIndex] = useState(0)
    const [coverUrl, setCoverUrl] = useState("")
    const [companyCoverUrl, setCompanyCoverUrl] = useState("")

    useEffect(() => {
        locales("fr")
    }, [])
    useEffect(() => {
        if (!onCloseProp) window.scrollTo({top: 0, behavior: "smooth"})
    }, [eid, onCloseProp])
    useEffect(() => {
        if (eid && eid !== "undefined") setVisit(eid).catch(console.error)
    }, [eid])

    //  Data loading: détails de l'événement + de la société

    useEffect(() => {
        if (!eid || eid === "undefined") return
        setLoading(true)
        const isPartner = xml === "1"
        const fetchFn = isPartner ? getEventPartnerDetail : getEventDetail

        fetchFn(eid)
            .then((res) => {
                const d = res.data[0]
                setEvent({
                    title: d.ev_name ?? "",
                    description: d.details ?? "",
                    imageName: d.image_name ?? "",
                    partnerImage: isPartner ? (d.imgPathLarge ?? "").replace("/300/", "/600/") : "",
                    tarif: d.tarif ?? "", tarifPromo: d.tarif_promo ?? "",
                    dtFrom: d.dt_from ?? "", dtTo: d.dt_to ?? "",
                    isXml: d.is_xml ?? "", isFree: d.is_free ?? false,
                    deeplink: d.deeplink ?? "", updatedAt: d.updated_at ?? "",
                    position: d.lat && d.lng ? {lat: d.lat, lng: d.lng} : null,
                    isFirebase: d.is_firebase === 1 || d.is_firebase === "1",
                })
                setCompany({
                    name: d.c_name || d.cname || "", phone: d.phone ?? "",
                    detail: d.detail ?? "", address: d.address ?? "",
                    city: d.city ?? "", id: d.cid ?? null,
                })
                console.log("set CID -->", d.cid)
                setCid(d.cid ?? null)
                document.title = d.ev_name || "Détail"
            })
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [eid, xml])

    // Image loading (Firebase or legacy OVH)
    useEffect(() => {
        if (loading) return
        const isPartner = xml === "1"
        const loadImages = async () => {
            // Partenaire : image unique
            if (isPartner) {
                const url = event.partnerImage
                setCoverUrl(url)
                if (url) setMediaList([{id: "cover", uri: url, type: "image", isCover: true}])
                return
            }

            // Firebase
            if (event.isFirebase) {
                try {
                    // Firebase
                    const response = await getImageByEvent(eid)
                    if (response?.data?.images?.length > 0) {
                        const cover = getFullCoverImageUrl(response.data)
                        const gallery = getGalleryImagesUrl(response.data)
                        setCoverUrl(cover?.url || "")
                        const allMedia = [
                            cover && {id: "cover", uri: cover.url, type: detectMediaType(cover.url, cover.mime || ""), isCover: true},
                            ...gallery.map((m, i) => ({id: m.id || `gal_${i}`, uri: m.url, type: detectMediaType(m.url, m.mime || ""), isCover: false})),
                        ].filter(Boolean)
                        setMediaList(allMedia)
                        return
                    }

                    // Fallback OVH si Firebase vide
                    const fallback = buildEventImageUrl(xml, eid, event.imageName, event.partnerImage, event.updatedAt)
                    setCoverUrl(fallback)
                    setMediaList([{id: "cover", uri: fallback, type: "image", isCover: true}])
                } catch (err) {
                    console.error("Firebase images error:", err)
                    const fallback = buildEventImageUrl(xml, eid, event.imageName, event.partnerImage, event.updatedAt)
                    setCoverUrl(fallback)
                    setMediaList([{id: "cover", uri: fallback, type: "image", isCover: true}])
                }
                return
            }

            // Legacy OVH
            const url = buildEventImageUrl(xml, eid, event.imageName, event.partnerImage, event.updatedAt)
            setCoverUrl(url)
            if (url) setMediaList([{id: "cover", uri: url, type: "image", isCover: true}])
        }

        loadImages()
    }, [loading, xml, eid, event.isFirebase, event.imageName, event.partnerImage, event.updatedAt])


    /**
     * Note :  get society image and galery after getting the cid
     */
    useEffect(() => {
        if (cid) {
            getImageByCompany(cid)
                .then(list => {
                    if (list.data && Array.isArray(list.data.images) && list.data.images.length > 0) {
                        const cover = getFullCoverImageUrl(list.data)
                        const gallery = getGalleryImagesUrl(list.data).map(img => ({
                            ...img,
                        }))
                        console.log("Company images from Firebase:", {cover, gallery})

                        const allMedia = [
                            // cover && {id: "cover", uri: cover.url, type: detectMediaType(cover.url, cover.mime || ""), isCover: true},
                            ...gallery.map((m, i) => ({id: m.id || `gal_${i}`, uri: m.url, type: detectMediaType(m.url, m.mime || ""), isCover: false})),
                        ].filter(Boolean)
                        setSocietyMediaList(allMedia)
                        setCompanyCoverUrl(cover?.url || "")
                    }
                })
                .catch(error => {
                    console.error('Erreur récupération images:', error)
                })
        }

    }, [cid])

    const isPartner = xml === "1"
    const companyImageUrl = useMemo(() => buildCompanyImageUrl(company.id), [company.id])

    const dateLabel = useMemo(() => formatEventDate(xml, event.dtFrom, event.dtTo), [xml, event.dtFrom, event.dtTo])
    const priceLabel = useMemo(() => buildPriceLabel(event.isFree, event.tarif, event.tarifPromo), [event.isFree, event.tarif, event.tarifPromo])
    const shareUrl = `https://justnow.app/event/event.php?id=${eid}&type=${event.isXml}`
    const parsedDesc = useMemo(() => event.description ? parse(cleanText(event.description), {trim: true}) : null, [event.description])
    const parsedCompany = useMemo(() => company.detail ? parse(cleanText(company.detail), {trim: true}) : null, [company.detail])

    const handleReport = useCallback(() => {
        reportAnEvent(eid).then(() => {
            setIsReported(true);
            setShowReportConfirm(false)
        }).catch(console.error)
    }, [eid])

    const handleClose = useCallback(() => {
        if (onCloseProp) onCloseProp()
        else window.close()
    }, [onCloseProp])

    const handleOpenEventLightbox = useCallback((index) => {
        setLightboxIndex(index)
        setLightboxImages(mediaList)
    }, [mediaList])

    const handleOpenSocietyLightbox = useCallback((index) => {
        setLightboxIndex(index)
        setLightboxImages(societyMediaList)
    }, [societyMediaList])

    const handleCloseLightbox = useCallback(() => setLightboxImages(null), [])

    // Render

    if (loading) {
        return (<div className="dt-loading">
            <div className="dt-loading__spinner"/>
            <p>Chargement</p></div>)
    }

    return (
        <div className="dt-page">
            <div className="dt-page__backdrop" style={{backgroundImage: `url(${coverUrl})`}}/>

            <article className="dt-card">
                {/* Cover PORTRAIT */}
                {/* Cover + Thumbnails */}
                <header className="dt-card__header">
                    {coverUrl ? (
                        <div className={`dt-card__hero ${mediaList.length > 1 ? "dt-card__hero--with-thumbs" : ""}`}>
                            {/* Cover principale */}
                            <div
                                className={`dt-card__cover--portrait${mediaList.length <= 1 ? " dt-card__cover--centered" : ""}`}
                                onClick={() => handleOpenEventLightbox(0)}
                                role="button"
                                tabIndex={0}
                            >
                                <img className="dt-card__cover-img dt-card__cover--portrait" src={coverUrl} alt={event.title} loading="lazy"/>
                                <div className="dt-card__cover-gradient"/>
                                {priceLabel && (
                                    <div className="dt-card__price">
                                        {typeof priceLabel === "object" ? (
                                            <><span className="dt-card__price--promo">{priceLabel.promo}</span><span className="dt-card__price--original">{priceLabel.original}</span></>
                                        ) : (<span>{priceLabel}</span>)}
                                    </div>
                                )}
                            </div>

                            {/* Miniatures verticales à droite */}
                            {mediaList.length > 1 && (
                                <div className="dt-card__thumbs">
                                    {mediaList.slice(1, 5).map((media, index) => (
                                        <button
                                            key={media.id || index + 1}
                                            className="dt-card__thumb"
                                            onClick={() => handleOpenEventLightbox(index + 1)}
                                        >
                                            <img src={media.thumbnail || media.uri} alt={`Media ${index + 2}`} loading="lazy"/>
                                            {media.type === "video" && (
                                                <div className="dt-card__thumb-play">
                                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                                                        <polygon points="5 3 19 12 5 21 5 3"/>
                                                    </svg>
                                                </div>
                                            )}
                                            {/* Indicateur "+N" sur la dernière miniature si d'autres médias existent */}
                                            {index === 3 && mediaList.length > 5 && (
                                                <div className="dt-card__thumb-more">
                                                    +{mediaList.length - 5}
                                                </div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="dt-card__cover dt-card__cover--placeholder">
                            <svg className="dt-card__placeholder-icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round"
                                 strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                <circle cx="8.5" cy="8.5" r="1.5"/>
                                <polyline points="21 15 16 10 5 21"/>
                            </svg>
                            <span className="dt-card__placeholder-text">Aucune image disponible</span>
                            {priceLabel && (
                                <div className="dt-card__price">
                                    {typeof priceLabel === "object" ? (
                                        <><span className="dt-card__price--promo">{priceLabel.promo}</span><span className="dt-card__price--original">{priceLabel.original}</span></>
                                    ) : (<span>{priceLabel}</span>)}
                                </div>
                            )}
                        </div>
                    )}
                    <button className="dt-card__close" onClick={handleClose} aria-label="Fermer">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </header>

                <div className="dt-card__body">
                    <time className="dt-card__date">{dateLabel}</time>
                    <h1 className="dt-card__title">{event.title}</h1>
                    {isPartner && event.deeplink && (
                        <a href={event.deeplink} className="dt-card__cta" target="_blank" rel="noopener noreferrer">Réserver maintenant â†’</a>
                    )}
                    <div className="dt-card__desc">{parsedDesc}</div>
                </div>

                {mediaList.length > 1 && <GalleryStrip mediaList={mediaList} onOpen={handleOpenEventLightbox}/>}
                {company.name && (
                    <section className="dt-company">
                        <div className="dt-company__row">
                            <div className="dt-company__info">
                                <h2 className="dt-company__name">{company.name}</h2>
                                <address className="dt-company__addr">{company.address}</address>
                                {!isPartner && company.phone && (
                                    <a href={`tel:${company.phone}`} className="dt-company__phone">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path
                                                d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                                        </svg>
                                        Appeler
                                    </a>
                                )}
                                {parsedCompany && <div className="dt-company__detail">{parsedCompany}</div>}
                                {isPartner &&
                                    <p className="dt-company__note">Premier acteur de la distribution de billets de spectacles, d'événements sportifs et de parcs d'attractions en France.</p>}
                            </div>
                            {!isPartner && companyImageUrl && (
                                <div className="dt-company__img-wrap dt-company__img-wrap--landscape">
                                    <img className="dt-company__img" src={companyCoverUrl || companyImageUrl} alt={company.name} loading="lazy"/>
                                </div>
                            )}
                        </div>
                        {societyMediaList.length > 0 && (
                            <GalleryStrip mediaList={societyMediaList} onOpen={handleOpenSocietyLightbox}/>
                        )}
                    </section>
                )}


                <section className="dt-sharing">
                    <span className="dt-sharing__label">Partager</span>
                    <div className="dt-sharing__buttons">
                        <FacebookShareButton url={shareUrl} quote={event.title} className="dt-sharing__btn dt-sharing__btn--facebook"><FacebookIcon size={32} round/></FacebookShareButton>
                        <WhatsappShareButton url={shareUrl} title={event.title} className="dt-sharing__btn dt-sharing__btn--whatsapp"><WhatsappIcon size={32} round/></WhatsappShareButton>
                        <TumblrShareButton url={shareUrl} title={event.title} className="dt-sharing__btn dt-sharing__btn--tumblr"><TumblrIcon size={32} round/></TumblrShareButton>
                    </div>
                    <button className={`dt-sharing__report ${isReported ? "dt-sharing__report--done" : ""}`} onClick={() => !isReported && setShowReportConfirm(true)} disabled={isReported}>
                        {isReported ? (<>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                            </svg>
                            Signalé</>) : (<>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/>
                                <line x1="4" y1="22" x2="4" y2="15"/>
                            </svg>
                            Signaler</>)}
                    </button>
                </section>

                {showReportConfirm && (
                    <div className="dt-confirm-overlay" onClick={() => setShowReportConfirm(false)}>
                        <div className="dt-confirm" onClick={(e) => e.stopPropagation()}>
                            <div className="dt-confirm__icon">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                    <line x1="12" y1="9" x2="12" y2="13"/>
                                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                                </svg>
                            </div>
                            <h3>êtes-vous certain·e ?</h3>
                            <p>Vous allez signaler cet événement comme inapproprié.</p>
                            <div className="dt-confirm__actions">
                                <button className="dt-confirm__cancel" onClick={() => setShowReportConfirm(false)}>Annuler</button>
                                <button className="dt-confirm__ok" onClick={handleReport}>Oui, signaler</button>
                            </div>
                        </div>
                    </div>
                )}

                {event.position ? (<section className="dt-map"><MiniMap Position={event.position}/></section>) : (<p className="dt-map__loading">Chargement de la carteâ€¦</p>)}
            </article>

            {lightboxImages && (
                <GalleryLightbox
                    images={lightboxImages}
                    initialIndex={lightboxIndex}
                    onClose={handleCloseLightbox}
                />
            )}
        </div>
    )
}
